import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { ShowLoader } from '../../../../redux/actions/loader'
import { Grid, TextField, FormControlLabel, FormGroup, Paper, Typography, Card, CardContent, IconButton, Box } from '@mui/material';
import SuggestionInput from '../../../../components/suggestionInput/suggestionInput';
import { CustomBtn } from '../../../../components/button/button' 
import Switch from '@mui/material/Switch';
import { DeleteOutlined, CommentOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const MobileAnnounce = () => {
  const userToken = JSON.parse(sessionStorage.getItem('User_Data'))?.token || undefined;
  const url = useSelector((state) => state.Api);
  const dispatch = useDispatch();
  const [suggestions, setSuggestions] = useState();
  const [classes, setClasses] = useState([]);
  const [heading, setHeading] = useState('');
  const [expiryDate, setExpiryDate] = useState(null);
  const [studentComments, setStudentComments] = useState({}); // Changed from showCommentInput
  
  // Global settings for all students
  const [globalSettings, setGlobalSettings] = useState({
    nameSwitch: false,
    pictureSwitch: false,
    commentsSwitch: false
  });

  // Load students from sessionStorage when component mounts
  useEffect(() => {
    const storedStudents = sessionStorage.getItem('selectedStudents');
    if (storedStudents) {
      try {
        const parsedStudents = JSON.parse(storedStudents);
        setClasses(parsedStudents);
        
        // Initialize comments for each student
        const initialComments = {};
        parsedStudents.forEach(student => {
          // Use roll as key (not RollNum) since your data uses 'roll'
          initialComments[student.roll] = '';
        });
        setStudentComments(initialComments);
      } catch (error) {
        console.error('Error parsing stored students:', error);
      }
    }
  }, []);

  function removeStudent(item, ind) {
    const updatedStudents = classes.filter((student, index) => index !== ind);
    setClasses(updatedStudents);
    
    // Remove comment for deleted student - use 'roll' as key
    const updatedComments = { ...studentComments };
    delete updatedComments[item.roll];
    setStudentComments(updatedComments);
    
    // Update sessionStorage
    sessionStorage.setItem('selectedStudents', JSON.stringify(updatedStudents));
  }

  function suggestionhandleInputChange(e) {
    const newValue = e.target.value;
    const record = suggestions.find(item => item.roll === newValue);
    
    // Check if student already exists in the list
    const isStudentExists = classes.some(student => student.roll === record?.roll);
    
    if (record && !isStudentExists) {
      const updatedStudents = [...classes, record];
      setClasses(updatedStudents);
      
      // Initialize comment for new student - use 'roll' as key
      setStudentComments(prev => ({
        ...prev,
        [record.roll]: ''
      }));
      
      // Update sessionStorage
      sessionStorage.setItem('selectedStudents', JSON.stringify(updatedStudents));
    }
  }
  
  // Handle global switch changes
  const handleGlobalSwitchChange = (field) => (event) => {
    setGlobalSettings(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };
  
  // Handle heading change
  const handleHeadingChange = (event) => {
    setHeading(event.target.value);
  };
  
  // Handle individual comment change for each student
  const handleCommentChange = (rollNum) => (event) => {
    setStudentComments(prev => ({
      ...prev,
      [rollNum]: event.target.value
    }));
  };
  
  // Handle expiry date change
  const handleExpiryDateChange = (date) => {
    setExpiryDate(date);
  };
  
  // Save all data
  const handleSaveData = () => {
    // Validation
    if (!heading.trim()) {
      alert('Please enter a heading');
      return;
    }
    
    if (classes.length === 0) {
      alert('Please add at least one student');
      return;
    }
    
    if (!expiryDate) {
      alert('Please select an expiry date');
      return;
    }
    
    // Prepare student data with comments
    const studentData = classes.map(student => ({
      rollNum: student.roll,
      name: student.name,
      className: student.Class,
      section: student.Section || '',
      fatherName: student.Name_P || '',
      comment: globalSettings.commentsSwitch ? (studentComments[student.roll] || '') : ''
    }));
    
    const announcementData = {
      heading: heading,
      globalSettings: {
        nameSwitch: globalSettings.nameSwitch,
        pictureSwitch: globalSettings.pictureSwitch,
        commentsSwitch: globalSettings.commentsSwitch
      },
      students: studentData, // Send properly formatted student data
      totalStudents: classes.length,
      expiryDate: expiryDate.toISOString(),
      createdAt: new Date().toISOString(),
      status: 'active'
    };
    
    console.log('Announcement Data to Save:', announcementData);
    
    // API call to save the data
    dispatch(ShowLoader('1'));
    axios.post(url.studentPromote, announcementData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userToken}`,
      },
    })
    .then((res) => {
      if (res.status === 200) {
        alert('Announcement saved successfully!');
        // Reset form
        setHeading('');
        setClasses([]);
        setExpiryDate(null);
        setGlobalSettings({
          nameSwitch: false,
          pictureSwitch: false,
          commentsSwitch: false
        });
        setStudentComments({});
        sessionStorage.removeItem('selectedStudents');
      }
      dispatch(ShowLoader('0'));
    })
    .catch((err) => {
      console.error('Error saving data', err);
      alert(err.response?.data?.message || 'Error saving announcement');
      dispatch(ShowLoader('0'));
    });
  };
  
  // Load students from API (only if not in sessionStorage)
  function callStudents() {
    // Check if students already exist in sessionStorage
    const storedStudents = sessionStorage.getItem('students');
    if (storedStudents && JSON.parse(storedStudents).length > 0) {
      setSuggestions(JSON.parse(storedStudents));
      alert('Students are already loaded from session storage!');
      return;
    }
    
    try {
      dispatch(ShowLoader('1'))
      axios.get(url.studentCards, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
      })
        .then((res) => {
          if (res.status === 200) {
            setSuggestions(res.data.message);
            dispatch(ShowLoader('0'))
          }
        })
        .catch((err) => {
          console.error(err);
          dispatch(ShowLoader('0'))
        });
    } catch (err) {
      console.error(err);
      dispatch(ShowLoader('0'))
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div style={{ padding: '20px' }}>
        <Typography variant="h5" gutterBottom>
          Mobile Announcement
        </Typography>
        
        {/* Heading Input */}
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} xs={12}>
            <TextField
              fullWidth
              label="Heading"
              variant="outlined"
              value={heading}
              onChange={handleHeadingChange}
              placeholder="Enter announcement heading..."
              required
            />
          </Grid>
          
          {/* Expiry Date Picker */}
          <Grid item lg={6} md={6} xs={12}>
            <DatePicker
              label="Expiry Date"
              value={expiryDate}
              onChange={handleExpiryDateChange}
              renderInput={(params) => <TextField {...params} fullWidth required />}
              minDate={dayjs()}
              disablePast
            />
          </Grid>
          
          {/* Global Settings Section */}
          <Grid item lg={12} md={12} xs={12}>
            <Paper elevation={2} style={{ padding: '15px', marginTop: '15px' }}>
              <Typography variant="h6" gutterBottom>
                Global Settings (Apply to All Students)
              </Typography>
              
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Switch
                      checked={globalSettings.nameSwitch}
                      onChange={handleGlobalSwitchChange('nameSwitch')}
                      color="primary"
                    />
                  }
                  label="Show Name"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={globalSettings.pictureSwitch}
                      onChange={handleGlobalSwitchChange('pictureSwitch')}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'red',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'red',
                        },
                      }}
                    />
                  }
                  label="Show Picture"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={globalSettings.commentsSwitch}
                      onChange={handleGlobalSwitchChange('commentsSwitch')}
                      color="warning"
                    />
                  }
                  label="Show Comments"
                />
              </FormGroup>
            </Paper>
          </Grid>
          
          {/* Student Selection Section */}
          <Grid item lg={12} md={12} xs={12}>
            <Paper elevation={2} style={{ padding: '15px', marginTop: '15px' }}>
              <Typography variant="h6" gutterBottom>
                Add Students
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item lg={8} md={8} xs={12}>
                  <SuggestionInput
                    handleInputChange={suggestionhandleInputChange}
                    Suggestions={suggestions}
                    placeholder='Student Roll No / Name'
                    name='RollNum'
                  />
                </Grid>
                <Grid item lg={4} md={4} xs={12}>
                  <CustomBtn 
                    type="button" 
                    click={() => callStudents()} 
                    data="Load Students" 
                    fullWidth
                  />
                </Grid>
              </Grid>
              
              <Typography variant="body1" style={{ marginTop: '15px', fontWeight: 'bold' }}>
                Total Students: {classes.length}
              </Typography>
            </Paper>
          </Grid>
          
          {/* Students Grid - 3 cards per row */}
          {classes.length > 0 && (
            <Grid item lg={12} md={12} xs={12}>
              <Paper elevation={2} style={{ padding: '15px', marginTop: '15px' }}>
                <Typography variant="h6" gutterBottom>
                  Students List
                </Typography>
                
                <Grid container spacing={2}>
                  {classes.map((value, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
                      <Card 
                        elevation={3} 
                        style={{ 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          backgroundColor: '#fafafa'
                        }}
                      >
                        <IconButton 
                          color="secondary" 
                          onClick={() => removeStudent(value, index)}
                          style={{ 
                            position: 'absolute', 
                            top: 5, 
                            right: 5,
                            backgroundColor: 'rgb(255, 255, 255)'
                          }}
                          size="small"
                        >
                          <DeleteOutlined />
                        </IconButton>
                        
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {value.name} {value.Name_P}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            <strong>Roll No:</strong> {value.roll}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            <strong>Class:</strong> {value.Class}
                          </Typography>
                          {value.Section && (
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>Section:</strong> {value.Section}
                            </Typography>
                          )}
                          
                          {/* Individual Comment Input for each student */}
                          {globalSettings.commentsSwitch && (
                            <Box mt={2}>
                              <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Comments"
                                placeholder="Enter comments for this student..."
                                value={studentComments[value.roll] || ''}
                                onChange={handleCommentChange(value.roll)}
                                multiline
                                rows={2}
                                InputProps={{
                                  startAdornment: (
                                    <CommentOutlined style={{ marginRight: 8, color: '#ffbb00' }} />
                                  ),
                                }}
                              />
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}
          
          {/* Save Button */}
          {classes.length > 0 && (
            <Grid item lg={12} md={12} xs={12}>
              <CustomBtn 
                type="button" 
                click={handleSaveData} 
                data="Save Announcement" 
                variant="contained" 
                color="primary"
                fullWidth
                style={{ marginTop: '20px' }}
              />
            </Grid>
          )}
        </Grid>
      </div>
    </LocalizationProvider>
  )
}

MobileAnnounce.propTypes = {}

export default MobileAnnounce